import { escapeCsvValue } from "utils/utils";
import type { DesignScopeCatalogItem } from "./apis";

/**
 * FE-owned export header. Import accepts these labels (and legacy Uniformat/Masterformat).
 * UniFormat/MasterFormat Title columns are informational on import; codes drive resolution.
 */
export const DESIGN_SCOPE_ITEMS_CSV_HEADER =
  "Title,Description,UniFormat Code,UniFormat Title,MasterFormat Code,MasterFormat Title";

const INVALID_FILENAME_CHARS = /[\\/:*?"<>|]/g;

function stripControlCharacters(value: string): string {
  return Array.from(value)
    .filter((char) => (char.codePointAt(0) ?? 0) > 31)
    .join("");
}

function classificationsForSystem(
  item: DesignScopeCatalogItem,
  system: "UNIFORMAT" | "MASTERFORMAT"
) {
  return item.classifications.filter(
    (classification) => classification.system === system
  );
}

function classificationFieldCsvValue(
  item: DesignScopeCatalogItem,
  system: "UNIFORMAT" | "MASTERFORMAT",
  field: "code" | "name"
): string {
  return classificationsForSystem(item, system)
    .map((classification) => classification[field])
    .filter(Boolean)
    .join(", ");
}

/**
 * Blank import-template layout: instruction rows, then one header as the last
 * non-empty line. Drop that header and keep everything above as instructions.
 */
export function instructionLinesFromImportTemplate(
  templateText: string
): string[] {
  const lines = templateText.split(/\r?\n/);
  while (lines.length > 0 && lines[lines.length - 1].trim() === "") {
    lines.pop();
  }
  if (lines.length === 0) {
    throw new Error("Import template is empty");
  }
  return lines.slice(0, -1);
}

export function buildDesignScopeItemsCsv(
  items: DesignScopeCatalogItem[],
  instructionLines: string[] = []
): string {
  const rows = items.map((item) =>
    [
      escapeCsvValue(item.title),
      escapeCsvValue(item.description),
      escapeCsvValue(classificationFieldCsvValue(item, "UNIFORMAT", "code")),
      escapeCsvValue(classificationFieldCsvValue(item, "UNIFORMAT", "name")),
      escapeCsvValue(classificationFieldCsvValue(item, "MASTERFORMAT", "code")),
      escapeCsvValue(classificationFieldCsvValue(item, "MASTERFORMAT", "name"))
    ].join(",")
  );
  return [...instructionLines, DESIGN_SCOPE_ITEMS_CSV_HEADER, ...rows].join(
    "\n"
  );
}

export function designScopeItemsExportFilename(catalogName: string): string {
  const cleaned = stripControlCharacters(catalogName)
    .replace(INVALID_FILENAME_CHARS, "")
    .trim();
  return `${cleaned || "Design Scope List"}.csv`;
}
